import { t } from 'i18next'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutAppearance } from 'src/app/layout/form-layout/form-layout.types'
import { ReservedResourceF } from '../../../types/reservation-detail.types'
import { Grid, Typography } from '@mui/material'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import useRRBulkEditForm from './reserved-resources-bulk-edit-form.hook'
import { FormItem, FormNumericField, HICLoading } from 'hic-components'
import { Controller } from 'react-hook-form'
import { z } from 'zod'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import { useTenant } from 'src/app/hooks/context/context.hook'
import RateSingleSelect from 'src/app/components/form-fields/rate-single-select'
import { ChargeCategoriesSingleSelect } from 'src/app/components/form-fields/charge-categories-single-select'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { tomorrowStartOfDay } from 'src/utils/date'
import { useEffect, useMemo, useState } from 'react'
import useReservedResourcesBulkEditCRUD from './reserved-resources-bulk-edit-crud'
import { RateFragment } from 'src/api/api.generated'

type ReservedResourcesBulkEditFormProps = {
  appearance: FormLayoutAppearance
  propertyId: string
  reservedResources: ReservedResourceF[]
  onBack: () => void
  onSuccess?: () => Promise<void>
}

export default function ReservedResourcesBulkEditForm(props: ReservedResourcesBulkEditFormProps) {
  const { currency } = useTenant()
  const { appearance, reservedResources, propertyId, onBack, onSuccess } = props
  const { loading: loadingCRUD, children } = useReservedResourcesBulkEditCRUD(propertyId, onSuccess)

  const [selectedRate, setSelectedRate] = useState<RateFragment>()
  const today = useMemo(() => new Date(), [])
  const tomorrow = useMemo(() => tomorrowStartOfDay(), [])
  const resourceTypes = useMemo(
    () =>
      reservedResources
        .map((rr) => {
          const tmpRtId: string[] = []
          rr.resourceOccupancies.forEach((ro) => {
            if (!tmpRtId.includes(ro.reservedResourceType.id)) {
              tmpRtId.push(ro.reservedResourceType.id)
            }
          })
          return tmpRtId
        })
        .flat(),
    [reservedResources],
  )
  const chargeCategories = useMemo(() => {
    const ratesChargeCategories = reservedResources.map((rr) => rr.rate.chargesRate?.map((cr) => cr.chargeCategoryId).flat() ?? [])
    const tmpChargeCategories: string[] = []
    ratesChargeCategories.flat().forEach((cc) => {
      const enabledCC = (ratesChargeCategories.filter((a) => a.includes(cc)) ?? []).length === ratesChargeCategories.length
      if (enabledCC && !tmpChargeCategories.includes(cc)) {
        tmpChargeCategories.push(cc)
      }
    })
    return tmpChargeCategories
  }, [reservedResources])
  const maxAdults = useMemo(
    () =>
      Math.min(
        ...reservedResources.map((rr) =>
          Math.min(...rr.resourceOccupancies.map((ro) => ro.reservedResourceType.maxAdults ?? ro.reservedResourceType.persons + ro.reservedResourceType.additionalPersons)),
        ),
      ),
    [reservedResources],
  )
  const maxOccupancy = useMemo(
    () => Math.min(...reservedResources.map((rr) => Math.min(...rr.resourceOccupancies.map((ro) => ro.reservedResourceType.persons + ro.reservedResourceType.additionalPersons)))),
    [reservedResources],
  )

  const { rrBulkEditForm, onFormSubmit, control, errors, watch, setValue, loadingSubmit } = useRRBulkEditForm(propertyId, reservedResources, maxAdults, maxOccupancy, onSuccess)
  type RRBulkEditForm = z.infer<typeof rrBulkEditForm>

  const occupancyError = useMemo(() => {
    const actualOccupancy = watch('adults') ?? 0 + (watch('children') ?? [])?.reduce((prev, curr) => (curr.noOccupancy ? 0 : prev + curr.quantity), 0)
    return actualOccupancy > maxOccupancy
  }, [watch('adults'), watch('children'), maxOccupancy])
  const occupancyErrorString = useMemo(() => (occupancyError ? t('pms.booking.reservation.rr_bulk_edit_dialog.max_occupancy_error', { maxOccupancy: maxOccupancy }) : undefined), [occupancyError])

  const adultsError = useMemo(() => (watch('adults') ?? 0) > maxAdults || occupancyError, [watch('adults'), occupancyError, maxAdults])
  const adultsErrorString = useMemo(() => (adultsError ? t('pms.booking.reservation.rr_bulk_edit_dialog.max_adults_error', { maxAdults: maxAdults }) : undefined), [adultsError])

  const [loading, setLoading] = useState(false)
  useEffect(() => {
    setLoading(loadingCRUD || loadingSubmit)
  }, [loadingCRUD, loadingSubmit])

  return (
    <FormLayout
      title={t('pms.booking.reservation.rr_bulk_edit_dialog.title')}
      appearance={appearance}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{ clickHandler: onFormSubmit, text: t('commons.apply'), loading: loading, disabled: loading }}
          cancelButton={{ clickHandler: onBack, text: t('commons.cancel'), disabled: loading }}
        />
      }
    >
      <Grid container spacing={1}>
        <Typography variant="bodyLarge" ml={1}>
          {t('pms.booking.reservation.rr_bulk_edit_dialog.subtitle', { count: reservedResources.length })}
        </Typography>
        <Grid item xs={12}>
          <Controller<RRBulkEditForm, 'pricePerNight'>
            name={'pricePerNight'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem //
                fieldLabel={t('pms.booking.reservation.rr_bulk_edit_dialog.price_per_night')}
                errorMessage={errors.pricePerNight?.message}
                fullWidth
              >
                <HicAmountField disabled={loading} currency={currency} value={value} onChange={onChange} />
              </FormItem>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller<RRBulkEditForm, 'rateId'>
            name={'rateId'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem //
                fieldLabel={t('pms.booking.reservation.rr_bulk_edit_dialog.rate')}
                errorMessage={errors.rateId?.message}
                fullWidth
              >
                <RateSingleSelect
                  disabled={loading}
                  value={value}
                  onChange={onChange}
                  onChangeFragment={(rate) => {
                    setSelectedRate(rate)
                    const chargeCategoryIsCompatible = watch('chargeCategoryId') && Boolean(rate?.chargesRate?.find((cr) => cr.chargeCategoryId === watch('chargeCategoryId')))
                    if (!chargeCategoryIsCompatible) {
                      setValue('chargeCategoryId', undefined)
                    }
                  }}
                  propertyId={propertyId}
                  filterRates={(rate) => {
                    const tmpEnableRt = resourceTypes.filter((rtId) => rate.resourcesTypeEnabledId?.includes(rtId))
                    return tmpEnableRt.length === resourceTypes.length
                  }}
                />
              </FormItem>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller<RRBulkEditForm, 'chargeCategoryId'>
            name={'chargeCategoryId'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem //
                fieldLabel={t('pms.booking.reservation.rr_bulk_edit_dialog.charge_category')}
                errorMessage={errors.chargeCategoryId?.message}
                fullWidth
              >
                <ChargeCategoriesSingleSelect
                  disabled={loading}
                  stayIncluded
                  value={value}
                  onChange={onChange}
                  propertyId={propertyId}
                  filterChargeCategory={(cc) => {
                    if (selectedRate) {
                      return Boolean(selectedRate.chargesRate?.find((c) => c.chargeCategoryId === cc.id))
                    } else {
                      return chargeCategories.includes(cc.id)
                    }
                  }}
                />
              </FormItem>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller<RRBulkEditForm, 'checkIn'>
            name={'checkIn'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem //
                fieldLabel={t('pms.booking.reservation.rr_bulk_edit_dialog.arrival')}
                errorMessage={errors.checkIn?.message}
                fullWidth
              >
                <DatePicker //
                  disabled={loading}
                  value={value ?? null}
                  onChange={(date) => {
                    if (typeof date !== 'string') {
                      onChange(date)
                    }
                  }}
                  minDate={today}
                  maxDate={watch('checkOut') ?? undefined}
                  slotProps={{ textField: { disabled: loading } }}
                />
              </FormItem>
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Controller<RRBulkEditForm, 'checkOut'>
            name={'checkOut'}
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormItem //
                fieldLabel={t('pms.booking.reservation.rr_bulk_edit_dialog.departure')}
                errorMessage={errors.checkOut?.message}
                fullWidth
              >
                <DatePicker //
                  disabled={loading}
                  value={value ?? null}
                  onChange={(date) => {
                    if (typeof date !== 'string') {
                      onChange(date)
                    }
                  }}
                  minDate={watch('checkIn') ?? tomorrow}
                  slotProps={{ textField: { disabled: loading } }}
                />
              </FormItem>
            )}
          />
        </Grid>

        <Grid item container xs={12} spacing={1}>
          <Controller<RRBulkEditForm, 'children'>
            name={'children'}
            control={control}
            render={({ field: { value: childrenValue, onChange: onChangeChildren } }) => (
              <>
                <Controller<RRBulkEditForm, 'adults'>
                  name={'adults'}
                  control={control}
                  render={({ field: { value: adultsValue, onChange: onChangeAdults } }) => (
                    <Grid item xs={6} sm={Math.floor(12 / (children.length + 1))}>
                      <FormNumericField
                        errorMessage={errors.pricePerNight?.message ?? adultsValue ? adultsErrorString : occupancyErrorString}
                        fieldLabel={t('pms.booking.reservation.rr_bulk_edit_dialog.adults')}
                        error={adultsError}
                        disabled={loading}
                        value={adultsValue ?? null}
                        onChange={onChangeAdults}
                      />
                    </Grid>
                  )}
                />
                {[
                  ...(
                    children?.map((child) => {
                      const tmpValue = childrenValue?.find((v) => v.childId === child.childId)?.quantity ?? null
                      const calculateNewChildren = (newValue: number | null) => {
                        if (newValue === null) {
                          onChangeChildren(childrenValue?.filter((v) => v.childId !== child.childId) ?? [])
                          return
                        }

                        if (childrenValue?.find((v) => v.childId === child.childId)) {
                          onChangeChildren(
                            childrenValue?.map((v) => {
                              return {
                                ...v,
                                quantity: v.childId === child.childId ? newValue : childrenValue,
                              }
                            }),
                          )
                        } else {
                          const tmpChildren = [...(childrenValue ?? [])]
                          tmpChildren.push({
                            childId: child.childId,
                            noOccupancy: child.noOccupancy,
                            quantity: newValue,
                          })
                          onChangeChildren(tmpChildren)
                        }
                      }
                      return (
                        <Grid key={child.childId} item xs={6} sm={Math.floor(12 / (children.length + 1))}>
                          <FormNumericField
                            fieldLabel={child.description}
                            errorMessage={(errors.children as any)?.message ?? child.noOccupancy ? undefined : occupancyErrorString}
                            error={occupancyError}
                            disabled={loading}
                            value={tmpValue}
                            onChange={calculateNewChildren}
                          />
                        </Grid>
                      )
                    }) ?? []
                  ).reverse(),
                ]}
              </>
            )}
          />
          {loading && <HICLoading />}
        </Grid>
      </Grid>
    </FormLayout>
  )
}
